import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDeviceSelectors } from 'react-device-detect';
import { Button } from 'grab-ui';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import scribeTrack from 'scribe/scribe-track';
import instrumentationDuck from 'ducks/instrumentation-duck';
import {
  GRAB_APP_FLAG_NAME,
  GRAB_APP_FLAG_RESULT,
} from 'constants/app-constants';
import GrabLogo from 'public/icon-192x192.png';
import closeSvg from 'public/images/banner-close.svg';
import fullStarSvg from 'public/images/full-star.svg';
import halfStarSvg from 'public/images/half-star.svg';
import styles from './installation-banner.less';

const STORAGE_KEY = 'INSTALLATION_BANNER_CLOSED';
const APP_VERSION = '1';

const useOldSearchParams = location =>
  new URLSearchParams(location.search || '');

const InstallationBanner = ({ link }) => {
  const location = useLocation();
  const searchParams = useOldSearchParams(location);
  const { orderId } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );

  const [deviceSelector] = useDeviceSelectors();
  const [isVisible, setIsVisible] = useState(
    () => localStorage.getItem(STORAGE_KEY) !== APP_VERSION,
  );

  const hideBanner =
    !deviceSelector.isMobile ||
    !isVisible ||
    searchParams.get(GRAB_APP_FLAG_NAME) === GRAB_APP_FLAG_RESULT;

  useEffect(
    () => {
      if (!hideBanner)
        scribeTrack.expressAcquisitionBanner.default({
          orderIdDisplayed: orderId,
        });
    },
    [hideBanner, orderId],
  );

  const text = useMemo(
    () => {
      if (deviceSelector.isAndroid)
        return I18n.t('banner.installation.android');

      if (deviceSelector.isIOS) return I18n.t('banner.installation.ios');

      return {
        title: '',
        install: '',
      };
    },
    [deviceSelector.isAndroid, deviceSelector.isIOS],
  );

  const ratingGroup = useMemo(
    () => {
      const group = [];

      const rating = _get(
        window.Grab.config,
        ['grabAppRating', deviceSelector.osName.toLowerCase()],
        4.5,
      );

      const innerRating = rating > 5 ? 5 : rating;

      const fullStarCount = Math.trunc(innerRating);

      Array.from({ length: fullStarCount }).forEach((_, index) =>
        group.push(
          <img
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={styles.star}
            src={fullStarSvg}
            alt="full star"
          />,
        ),
      );

      const remainderStar = innerRating - fullStarCount;

      if (remainderStar > 0)
        group.push(
          <img
            // eslint-disable-next-line react/no-array-index-key
            key={group.length}
            className={styles.star}
            src={halfStarSvg}
            alt="half star"
          />,
        );

      return group;
    },
    [deviceSelector.osName],
  );

  const handleOnClose = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, APP_VERSION);
    setIsVisible(false);
  }, []);

  const handleClickInstall = useCallback(
    () => {
      scribeTrack.expressAcquisitionBanner.click({
        orderIdDisplayed: orderId,
      });
      if (link) {
        window.location.href = link;
      } else {
        // NOTE:: Always fallback to this link (prevent missing grapAppLink field in config.json - only for ge-delivery)
        window.location.href = 'https://grab.onelink.me/2695613898/wibsa99w';
      }
    },
    [link, orderId],
  );

  if (hideBanner) return null;

  return (
    <div className={styles.container}>
      <Button
        className={styles.closeButton}
        onClick={handleOnClose}
        aria-label="close"
      >
        <img src={closeSvg} alt="close" width={12} height={12} />
      </Button>

      <div className={styles.logoContainer}>
        <img src={GrabLogo} alt="grab logo" width="100%" height="100%" />
      </div>

      <div className={styles.textContainer}>
        <p className={styles.title}>{text.title}</p>
        <div className={styles.starContainer}>{ratingGroup}</div>
      </div>

      <Button
        className={styles.installButton}
        onClick={handleClickInstall}
        aria-label="install"
      >
        {text.install}
      </Button>
    </div>
  );
};

InstallationBanner.propTypes = {
  link: PropTypes.string.isRequired,
};

export default InstallationBanner;
